import React from "react";
import { Controller } from "react-hook-form";
import {
  View,
  Text,
  Pressable,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
} from "react-native";
import { ActivityIndicator } from "react-native-paper";
import Header from "../../components/Header";
import AdminInput from "../../components/AdminInput";
import AdminSwitch from "../../components/AdminSwitch";
import AdminAlert from "../../components/AdminAlert";
import ViewportProvider from "../../hooks/ViewPortProvider";
import { useMyAccount } from "./useMyAccount";
import { ADMIN_COLORS, ADMIN_FONT } from "../../config/theme/adminColors";
import {
  SectionCard,
  Field,
  FieldLabel,
  FieldError,
} from "../AdminPanel/Products/formStyles";
import Toast from "react-native-toast-message";
import toastConfig from "../../components/Toast/toast.config";
import { useCustomTheme } from "../../contexts/useCustomTheme";
import { useRemoteConfigContext } from "../../contexts/useRemoteConfigContext";
import { useAuthUser } from "../../contexts/useAuthUser";

export function MyAccount({ navigation }) {
  const {
    onSubmit,
    control,
    errors,
    handleSubmit,
    isLoadingUserInfo,
    isPendingUpdatedUserInfo,
    setValue,
    isUrlValid,
    auth,
    termsOfUseUrl,
    setTermsOfUseUrl,
    webUrl,
    setWebUrl,
    salesPageUrl,
    setSalesPageUrl,
    formEmbed,
    setFormEmbed,
    androidUrl,
    setAndroidUrl,
    iosUrl,
    setIosUrl,
    myStoreUrl,
    setMyStoreUrl,
    hasMyStore,
    setHasMyStore,
    rtmpEmail,
    setRtmpEmail,
  } = useMyAccount();

  const user = auth.currentUser;

  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const { customInfo } = useCustomTheme();
  const config = useRemoteConfigContext();
  const hasForm = config.hasForm;
  const hasIAP = config.api_key_appl || config.api_key_goog;

  const planType = customInfo?.planType;
  const videoHosting = customInfo?.videoHosting;
  const hasLives = planType === "premium" && videoHosting === "ownPanda";

  // Quem pode editar: o ADMINISTRADOR do app (`users/{uid}.isAdmin`), a mesma
  // marca que já decide entre AdminRoutes e AppRoutes.
  //
  // Antes a tela exigia que o documento do usuário tivesse `appInformation`,
  // e quem não tinha via "Seu usuário não possui autorização...". Só que esse
  // campo NASCE do primeiro salvamento feito aqui: o administrador que ainda
  // não tinha salvo nada ficava travado — não podia salvar porque nunca salvou.
  // O documento lido é o do AuthUserProvider, resolvido no boot (sem releitura).
  const { user: userDoc } = useAuthUser();
  const canEditAppInfo = userDoc?.isAdmin === true;

  const urlRule = {
    validate: (value) =>
      !value ? true : isUrlValid(value) || "Insira uma URL válida",
  };

  // Campo controlado (react-hook-form). Invocado como função (não como <Componente/>)
  // para inlinar o JSX e manter a identidade dos inputs estável entre renders.
  const controlledField = ({
    name,
    label,
    placeholder,
    rules,
    error,
    stateValue,
    onState,
    last,
  }) => (
    <Field style={last ? { marginBottom: 0 } : undefined}>
      <FieldLabel>{label}</FieldLabel>
      <Controller
        control={control}
        name={name}
        rules={rules}
        render={({ field: { onChange, onBlur, value } }) => (
          <>
            <AdminInput
              placeholder={placeholder}
              onBlur={onBlur}
              onChangeText={(text) => {
                onChange(text);
                if (onState) onState(text);
              }}
              value={stateValue !== undefined ? stateValue || value : value}
            />
            {error ? <FieldError>{error.message}</FieldError> : null}
          </>
        )}
      />
    </Field>
  );

  const simpleField = ({ label, placeholder, value, onChangeText, last }) => (
    <Field style={last ? { marginBottom: 0 } : undefined}>
      <FieldLabel>{label}</FieldLabel>
      <AdminInput
        placeholder={placeholder}
        value={value}
        onChangeText={onChangeText}
      />
    </Field>
  );

  return (
    <ViewportProvider>
      <View style={{ flex: 1, backgroundColor: ADMIN_COLORS.surfaceSubtle }}>
        <Header navigation={navigation} user={user} isAdminPage />

        <ScrollView contentContainerStyle={styles.scrollContent}>
          <View style={styles.page}>
            {/* Cabeçalho da página */}
            <View style={styles.headerBlock}>
              <Text style={[styles.title, isMobile && { fontSize: 24 }]}>
                Minha conta
              </Text>
              <Text style={styles.subtitle}>
                Gerencie os dados da sua conta e as configurações do seu
                aplicativo.
              </Text>
            </View>

            {isLoadingUserInfo ? (
              <View style={styles.loadingBox}>
                <ActivityIndicator color={ADMIN_COLORS.teal} size="large" />
              </View>
            ) : (
              <>
                {/* Dados da conta */}
                <SectionCard
                  title="Dados da conta"
                  subtitle="Informações da sua conta de administrador."
                >
                  {controlledField({
                    name: "Nome_Completo",
                    label: "Nome completo",
                    placeholder: "Digite seu nome completo",
                    rules: { required: "Nome completo é obrigatório" },
                    error: errors.Nome_Completo,
                  })}
                  <Field style={{ marginBottom: 0 }}>
                    <FieldLabel>E-mail</FieldLabel>
                    <AdminInput value={user.email} editable={false} />
                  </Field>
                </SectionCard>

                {/* Identidade do aplicativo */}
                <SectionCard
                  title="Identidade do aplicativo"
                  subtitle="Nome, suporte e domínio do seu app."
                >
                  {controlledField({
                    name: "appInformation.appName",
                    label: "Nome do aplicativo",
                    placeholder: "Digite o nome do app",
                    rules: { required: "Nome do aplicativo é obrigatório" },
                    error: errors.appInformation?.appName,
                  })}
                  {controlledField({
                    name: "appInformation.supportContact",
                    label: "Contato de suporte para os alunos",
                    placeholder:
                      "Digite o contato de suporte (email ou telefone)",
                    rules: { required: "Contato para suporte é obrigatório" },
                    error: errors.appInformation?.supportContact,
                  })}
                  {controlledField({
                    name: "appInformation.webUrl",
                    label: "Domínio do app",
                    placeholder: "https://app.exemplo.com.br",
                    rules: urlRule,
                    error: errors.appInformation?.webUrl,
                    stateValue: webUrl,
                    onState: setWebUrl,
                  })}
                  {simpleField({
                    label: "URL dos seus termos de uso",
                    placeholder: "https://exemplo.com.br",
                    value: termsOfUseUrl,
                    onChangeText: setTermsOfUseUrl,
                    last: true,
                  })}
                </SectionCard>

                {/* Links e integrações */}
                <SectionCard
                  title="Links e integrações"
                  subtitle="Página de vendas, lojas de aplicativos e integrações."
                >
                  {simpleField({
                    label: "URL da sua página de vendas",
                    placeholder: "https://exemplo.com.br",
                    value: salesPageUrl,
                    onChangeText: setSalesPageUrl,
                  })}
                  {controlledField({
                    name: "appInformation.IOSUrl",
                    label: "URL do app na Apple",
                    placeholder: "Digite a URL",
                    rules: urlRule,
                    error: errors.appInformation?.IOSUrl,
                    stateValue: iosUrl,
                    onState: setIosUrl,
                  })}
                  {controlledField({
                    name: "appInformation.androidUrl",
                    label: "URL do app na Play Store",
                    placeholder: "Digite a URL do app na Play Store",
                    rules: urlRule,
                    error: errors.appInformation?.androidUrl,
                    stateValue: androidUrl,
                    onState: setAndroidUrl,
                    last: !hasForm && !hasIAP,
                  })}

                  {hasForm
                    ? simpleField({
                        label: "URL do formulário do Google",
                        placeholder: "https://docs.google.com/forms/",
                        value: formEmbed,
                        onChangeText: setFormEmbed,
                        last: !hasIAP,
                      })
                    : null}

                  {hasIAP ? (
                    <Field style={{ marginBottom: 0 }}>
                      <View style={styles.toggleRow}>
                        <FieldLabel>Habilitar loja online</FieldLabel>
                        <AdminSwitch
                          value={hasMyStore}
                          onValueChange={(value) => {
                            if (value === false) {
                              setValue("hasAppInfo", false);
                              setHasMyStore(false);
                            } else {
                              setHasMyStore(value);
                            }
                          }}
                        />
                      </View>
                      {hasMyStore ? (
                        <AdminInput
                          style={{ marginTop: 8 }}
                          placeholder="https://exemplo.com.br"
                          value={myStoreUrl}
                          onChangeText={(text) => setMyStoreUrl(text)}
                        />
                      ) : null}
                    </Field>
                  ) : null}
                </SectionCard>

                {/* Transmissão de lives (premium + Panda próprio) */}
                {hasLives ? (
                  <SectionCard
                    title="Transmissão de lives"
                    subtitle="Transmita lives pelo aplicativo mobile."
                  >
                    {simpleField({
                      label:
                        "E-mail do transmissor da live (precisa ser administrador)",
                      placeholder: "administrador@domínio.com",
                      value: rtmpEmail,
                      onChangeText: setRtmpEmail,
                      last: true,
                    })}
                  </SectionCard>
                ) : null}

                {/* Ação / autorização */}
                {canEditAppInfo ? (
                  <View style={styles.saveRow}>
                    <Pressable
                      onPress={handleSubmit(onSubmit)}
                      disabled={isPendingUpdatedUserInfo}
                      style={({ hovered }) => [
                        styles.primaryBtn,
                        isPendingUpdatedUserInfo && { opacity: 0.6 },
                        hovered &&
                          !isPendingUpdatedUserInfo && {
                            backgroundColor: ADMIN_COLORS.accentDark,
                          },
                      ]}
                    >
                      {isPendingUpdatedUserInfo ? (
                        <ActivityIndicator size="small" color="#FFFFFF" />
                      ) : (
                        <Text style={styles.primaryBtnText}>Salvar</Text>
                      )}
                    </Pressable>
                  </View>
                ) : (
                  <AdminAlert variant="error">
                    Seu usuário não possui autorização para alterar essas
                    informações. Somente o administrador principal pode realizar
                    essa alteração.
                  </AdminAlert>
                )}
              </>
            )}
          </View>
        </ScrollView>
      </View>
      <Toast config={toastConfig} />
    </ViewportProvider>
  );
}

const styles = StyleSheet.create({
  scrollContent: {
    flexGrow: 1,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
    padding: 24,
    alignItems: "center",
  },
  page: {
    width: "100%",
    maxWidth: 720,
  },
  headerBlock: {
    marginBottom: 20,
  },
  title: {
    fontFamily: ADMIN_FONT,
    fontSize: 28,
    fontWeight: "700",
    color: ADMIN_COLORS.ink,
  },
  subtitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    color: ADMIN_COLORS.muted,
    marginTop: 4,
  },
  loadingBox: {
    paddingVertical: 80,
    alignItems: "center",
    justifyContent: "center",
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  saveRow: {
    marginTop: 4,
    marginBottom: 8,
  },
  primaryBtn: {
    height: 44,
    borderRadius: 10,
    paddingHorizontal: 24,
    backgroundColor: ADMIN_COLORS.teal,
    alignItems: "center",
    justifyContent: "center",
    alignSelf: "flex-start",
    minWidth: 120,
    cursor: "pointer",
  },
  primaryBtnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 15,
    fontWeight: "600",
    color: "#FFFFFF",
  },
});
